import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, Users, CalendarDays, TrendingUp, Lock, Loader2 } from "lucide-react";

const SKEY = "kc_stats_key";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-brand-surface p-6">
    <div className="flex items-center justify-between">
      <span className="font-body text-sm text-white/55">{label}</span>
      <Icon className={`h-5 w-5 ${accent}`} />
    </div>
    <p className="mt-3 font-heading text-4xl font-extrabold text-white">{value}</p>
  </div>
);

export default function Stats() {
  const [key, setKey] = useState(localStorage.getItem(SKEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats?key=${encodeURIComponent(k)}`);
      if (res.status === 401) {
        setError("Falsches Passwort.");
        setData(null);
        localStorage.removeItem(SKEY);
        return;
      }
      if (!res.ok) throw new Error("Fehler");
      const json = await res.json();
      localStorage.setItem(SKEY, k);
      setData(json);
    } catch (e) {
      setError("Konnte Statistiken nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxViews = data ? Math.max(1, ...data.last7.map((d) => d.views)) : 1;

  return (
    <div className="min-h-screen bg-brand-bg font-body text-white">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-[68px] max-w-5xl items-center justify-between px-5 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/brand/logo.png" alt="Kurier Connect" className="h-9 w-9 rounded-md object-contain" />
            <span className="font-heading text-sm font-extrabold uppercase tracking-[0.14em] text-white">
              Kurier<span className="text-brand-green">Connect</span> · Stats
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white/70 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Zur Startseite
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-12 lg:px-8">
        <h1 className="font-heading text-3xl font-extrabold sm:text-4xl">Besucher-Statistik</h1>
        <p className="mt-2 font-body text-sm text-white/55">
          First-Party Reichweitenmessung deiner Landingpage – ohne Drittanbieter.
        </p>

        {!data && (
          <form
            onSubmit={(e) => { e.preventDefault(); load(key); }}
            className="mt-8 flex max-w-md flex-col gap-3 rounded-2xl border border-white/10 bg-brand-surface p-6"
          >
            <label className="flex items-center gap-2 font-heading text-sm font-semibold text-white/80">
              <Lock className="h-4 w-4 text-brand-green" /> Zugangs-Passwort
            </label>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              data-testid="stats-key-input"
              placeholder="Passwort eingeben"
              className="rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-body text-white outline-none focus:border-brand-green"
            />
            {error && <p className="font-body text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              data-testid="stats-submit"
              className="mt-1 rounded-full bg-brand-green px-5 py-3 font-heading text-sm font-bold uppercase tracking-wide text-brand-bg glow-green-sm"
            >
              {loading ? "Lädt …" : "Anzeigen"}
            </button>
          </form>
        )}

        {loading && data === null && (
          <div className="mt-10 flex items-center gap-3 text-white/60">
            <Loader2 className="h-5 w-5 animate-spin text-brand-green" /> Lädt …
          </div>
        )}

        {data && (
          <div className="mt-8 space-y-8" data-testid="stats-dashboard">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard icon={Eye} label="Aufrufe gesamt" value={data.total_views} accent="text-brand-green" />
              <StatCard icon={Users} label="Eindeutige Besucher" value={data.total_unique} accent="text-brand-blue" />
              <StatCard icon={CalendarDays} label="Aufrufe heute" value={data.today_views} accent="text-brand-green" />
              <StatCard icon={TrendingUp} label="Besucher heute" value={data.today_unique} accent="text-brand-magenta" />
            </div>

            <div className="rounded-2xl border border-white/10 bg-brand-surface p-6">
              <h2 className="font-heading text-lg font-bold">Letzte 7 Tage</h2>
              <div className="mt-6 flex items-end gap-3" style={{ height: 180 }}>
                {data.last7.map((d) => (
                  <div key={d.day} className="flex flex-1 flex-col items-center gap-2">
                    <span className="font-heading text-xs font-semibold text-white/70">{d.views}</span>
                    <div className="flex w-full items-end justify-center" style={{ height: 120 }}>
                      <div
                        className="w-full max-w-[42px] rounded-t-md bg-brand-green/80"
                        style={{ height: `${Math.round((d.views / maxViews) * 120)}px`, minHeight: d.views ? 4 : 0 }}
                        title={`${d.views} Aufrufe · ${d.unique} Besucher`}
                      />
                    </div>
                    <span className="font-body text-[10px] text-white/45">{d.day.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-brand-surface p-6">
              <h2 className="font-heading text-lg font-bold">Top-Herkunft</h2>
              <ul className="mt-4 space-y-2">
                {data.top_referrers.map((r, i) => (
                  <li key={i} className="flex items-center justify-between border-b border-white/5 py-2 last:border-0">
                    <span className="truncate font-body text-sm text-white/70">{r.referrer}</span>
                    <span className="font-heading text-sm font-bold text-white">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={() => load(key)}
              className="rounded-full border border-white/10 px-5 py-2.5 font-heading text-sm font-semibold text-white/80 hover:bg-white/5"
            >
              Aktualisieren
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
